import { inject, Injectable } from "@angular/core";
import { arePropertiesDefined, isPropertyDefined } from "@recursyve/nice-ts-utils";
import { NiceAsyncTypeaheadProvider, NiceAsyncTypeaheadSearchResult } from "@recursyve/nice-ui-kit.v2";
import { lastValueFrom, Observable } from "rxjs";
import { map } from "rxjs/operators";

import { ApiService } from "../../api.service";
import { FindOptionsDto } from "../../common/dto/find-options.dto";
import { FindMembersDto } from "../dto/find-members.dto";
import { MemberStatus } from "../enums/member-status.enum";
import { Members } from "../models/members.model";

export interface MemberTypeaheadOptions extends FindOptionsDto {
    organisationId: number;
    archived?: boolean;
    status?: MemberStatus;
}

@Injectable()
export class MemberTypeaheadApi extends NiceAsyncTypeaheadProvider<Members> {
    private readonly api = inject(ApiService);

    public readonly resource = "members";

    private readonly pageSize = 20;

    public override async getById(id: number): Promise<Members> {
        return lastValueFrom(this.api.members.getById(id));
    }

    public override search(
        searchQuery: string,
        page: number,
        options?: MemberTypeaheadOptions
    ): Observable<NiceAsyncTypeaheadSearchResult<Members>> {
        const dto: FindMembersDto = {
            searchQuery,
            page,
            pageSize: this.pageSize
        };

        if (isPropertyDefined(options, "organisationId")) {
            dto.organisationId = options.organisationId;
        }

        if (isPropertyDefined(options, "archived")) {
            dto.archived = options.archived;
        }

        if (isPropertyDefined(options, "ignoreIds")) {
            dto.ignoreIds = options.ignoreIds;
        }

        if (isPropertyDefined(options, "status")) {
            dto.status = options.status;
        }

        return this.api.members
            .search(dto)
            .pipe(map((items) => ({ items, nextPage: items.length === this.pageSize ? page + 1 : null })));
    }

    public override format(item: Members): string {
        if (!arePropertiesDefined(item, "email", "fullName")) {
            return "";
        }

        return `${item.fullName} (${item.email})`;
    }
}
